import React, { memo, useMemo, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { useFrame } from '../../utils/useFrameP1'; // P1: igual ao do r3f; com ?dbg=1 mede o tempo dos handlers
import { Text } from '@react-three/drei';
import { calculateStep, formatPrice, formatForce } from '../../utils/scaleHelper';
import { markEvent, EV } from '../../utils/jankRecorder';

// ---------------------------------------------------------------------------
// Marcas de escala ao vivo (linhas de grade + rótulos) que acompanham a escala a cada quadro.
//
// A escala vertical agora muda suavemente a cada quadro (visibleScale.ts). Linhas gravadas em
// textura e rótulos reconstruídos pelo React ficariam atrasados em relação às velas. Aqui tudo é
// imperativo, num conjunto FIXO de objetos criado uma vez:
//  - posições escritas por quadro (sem React, sem setState);
//  - texto Troika trocado SÓ quando o valor da marca daquele slot muda (dirty-check);
//  - passo das marcas com histerese (só recalcula se a faixa variar mais de 10%), sem tremor.
// Valor -> mundo: coordenada = valor * scale + offset no eixo escolhido.
// ---------------------------------------------------------------------------

const MAX_TICKS = 40;

export interface LiveScaleDomain {
  /** Escreve [mín, máx, scale, offset] e devolve false se a escala ainda não está pronta. */
  read: (out: Float64Array) => boolean;
}

interface LiveScaleTicksProps {
  domain: LiveScaleDomain;
  kind: 'price' | 'force';
  axis: 'y' | 'z';
  targetTicks?: number;
  /** Limites do mundo no eixo (as marcas fora deles somem). */
  bounds: [number, number];
  padding?: number;
  /** Linhas: extremos [x,y,z]; a coordenada do eixo é substituída pela da marca. */
  lineA?: [number, number, number];
  lineB?: [number, number, number];
  showLines?: boolean;
  lineColor?: string;
  lineOpacity?: number;
  /** Rótulos: grupo posicionado/rotacionado; dentro dele o rótulo anda no eixo local `labelAxis`. */
  showLabels?: boolean;
  labelGroupPosition?: [number, number, number];
  labelGroupRotation?: [number, number, number];
  labelAxis?: 'x' | 'y' | 'z';
  labelSign?: number;
  anchorX?: 'left' | 'center' | 'right';
  anchorY?: 'top' | 'top-baseline' | 'middle' | 'bottom-baseline' | 'bottom';
  baseSize?: number;
  color?: string;
  colorUp?: string;
  colorDown?: string;
  /** Inclui as marcas secundárias (meio passo). */
  secondary?: boolean;
}

export const LiveScaleTicks = memo(({
  domain, kind, axis, targetTicks = 10, bounds, padding = 8,
  lineA, lineB, showLines = false, lineColor = '#ffffff', lineOpacity = 0.15,
  showLabels = true, labelGroupPosition = [0, 0, 0], labelGroupRotation = [0, 0, 0], labelAxis = 'y', labelSign = 1,
  anchorX = 'right', anchorY = 'middle', baseSize = 20, color = '#ffffff', colorUp, colorDown, secondary = true,
}: LiveScaleTicksProps) => {
  const labelRefs = useRef<any[]>([]);
  const slotValue = useRef<Float64Array>(new Float64Array(MAX_TICKS).fill(NaN));
  const slotLevel = useRef<Int8Array>(new Int8Array(MAX_TICKS));
  const slotFrame = useRef<Int32Array>(new Int32Array(MAX_TICKS));
  const frameNo = useRef(0);
  const dom = useRef<Float64Array>(new Float64Array(4));
  const stepState = useRef({ step: 0, refRange: 0 });

  const lines = useMemo(() => {
    const mk = (opacity: number) => {
      const g = new THREE.BufferGeometry();
      const attr = new THREE.BufferAttribute(new Float32Array(MAX_TICKS * 2 * 3), 3);
      attr.setUsage(THREE.DynamicDrawUsage);
      g.setAttribute('position', attr);
      g.setDrawRange(0, 0);
      const m = new THREE.LineBasicMaterial({ color: new THREE.Color(lineColor), transparent: true, opacity, depthWrite: false, toneMapped: false });
      const l = new THREE.LineSegments(g, m);
      l.frustumCulled = false;
      l.renderOrder = -4;
      return l;
    };
    return { primary: mk(lineOpacity * 0.85), secondary: mk(lineOpacity * 0.45) };
  }, [lineColor, lineOpacity]);
  useEffect(() => () => {
    for (const l of [lines.primary, lines.secondary]) { l.geometry.dispose(); (l.material as THREE.Material).dispose(); }
  }, [lines]);

  useFrame(() => {
    const D = dom.current;
    if (!domain.read(D)) return;
    const vMin = D[0], vMax = D[1], scale = D[2], offset = D[3];
    const R = vMax - vMin;
    if (!(R > 0)) return;
    const st = stepState.current;
    if (!(st.step > 0) || R > st.refRange * 1.1 || R < st.refRange * 0.9) {
      st.step = calculateStep(R, targetTicks);
      st.refRange = R;
    }
    const step = st.step;
    const lo = Math.min(bounds[0], bounds[1]) + padding, hi = Math.max(bounds[0], bounds[1]) - padding;

    const pArr = (lines.primary.geometry.getAttribute('position') as THREE.BufferAttribute);
    const sArr = (lines.secondary.geometry.getAttribute('position') as THREE.BufferAttribute);
    const pa = pArr.array as Float32Array, sa = sArr.array as Float32Array;
    let np = 0, ns = 0, count = 0;
    const frame = ++frameNo.current;
    const ax = axis === 'y' ? 1 : 2;
    const half = secondary ? 2 : 1;
    const k0 = Math.ceil((vMin / step) * half - 1e-9), k1 = Math.floor((vMax / step) * half + 1e-9);
    for (let k = k0; k <= k1 && count < MAX_TICKS; k++) {
      const isPrimary = half === 1 || k % 2 === 0;
      const v = Number(((k / half) * step).toPrecision(12));
      const w = v * scale + offset;
      if (w < lo || w > hi) continue;
      // linhas
      if (showLines && lineA && lineB) {
        const arr = isPrimary ? pa : sa;
        const i = (isPrimary ? np++ : ns++) * 6;
        arr[i] = lineA[0]; arr[i + 1] = lineA[1]; arr[i + 2] = lineA[2];
        arr[i + 3] = lineB[0]; arr[i + 4] = lineB[1]; arr[i + 5] = lineB[2];
        arr[i + ax] = w; arr[i + 3 + ax] = w;
      }
      // rótulos: slot fixo por índice da marca (ao rolar, só a marca que entra troca de texto)
      const slot = ((k % MAX_TICKS) + MAX_TICKS) % MAX_TICKS;
      slotFrame.current[slot] = frame;
      if (showLabels) {
        const t = labelRefs.current[slot];
        if (t) {
          const level = isPrimary ? 1 : 2;
          if (slotValue.current[slot] !== v || slotLevel.current[slot] !== level) {
            slotValue.current[slot] = v;
            slotLevel.current[slot] = level;
            t.text = kind === 'price' ? formatPrice(v, isPrimary ? step : step / 2) : formatForce(v);
            t.fontSize = baseSize * (isPrimary ? 1.0 : 0.8);
            t.fillOpacity = isPrimary ? 0.95 : 0.65;
            if (kind === 'force') t.color = v > 0 && colorUp ? colorUp : v < 0 && colorDown ? colorDown : color;
            t.sync();
            markEvent(EV.TEXTO);
          }
          const local = labelSign * w;
          t.position.set(labelAxis === 'x' ? local : 0, labelAxis === 'y' ? local : 0, labelAxis === 'z' ? local : 0);
          t.visible = true;
        }
      }
      count++;
    }
    for (let i = 0; i < MAX_TICKS; i++) { if (slotFrame.current[i] !== frame) { const t = labelRefs.current[i]; if (t && t.visible) t.visible = false; } }
    lines.primary.geometry.setDrawRange(0, np * 2); pArr.needsUpdate = np > 0;
    lines.secondary.geometry.setDrawRange(0, ns * 2); sArr.needsUpdate = ns > 0;
    lines.primary.visible = showLines && np > 0;
    lines.secondary.visible = showLines && ns > 0;
  });

  const slots = useMemo(() => Array.from({ length: MAX_TICKS }, (_, i) => i), []);
  return (
    <>
      <primitive object={lines.primary} />
      <primitive object={lines.secondary} />
      {showLabels && (
        <group position={labelGroupPosition} rotation={labelGroupRotation}>
          {slots.map(i => (
            <Text key={i} ref={(el: any) => { labelRefs.current[i] = el; }} visible={false}
              fontSize={baseSize} color={color} anchorX={anchorX} anchorY={anchorY} fillOpacity={0.95}>
              {' '}
            </Text>
          ))}
        </group>
      )}
    </>
  );
});
